/**
 * API Vitrine — publication et lecture.
 *
 * Côté patron (auth requise) :
 *   - GET  /vitrine/me          → état actuel
 *   - PUT  /vitrine             → publie (slogan/horaires/description/active)
 *
 * Côté public (pour worker Vitrine futur ou page /v/:id de l'App) :
 *   - GET  /vitrine/public/:id  → profil + articles visibles
 */
import { tokens } from './api';
import type { ApiError } from './api';

const CORE_URL = (import.meta.env.VITE_CORE_URL as string | undefined) ?? '';

export interface VitrineMe {
  active: boolean;
  slogan: string | null;
  horaires: string | null;
  description: string | null;
}

export interface VitrinePublique {
  id: string;
  nom: string;
  quartier: string;
  ville: string;
  slogan: string | null;
  description: string | null;
  horaires: string | null;
  tel: string;
  articles: Array<{
    id: string;
    nom: string;
    prix: number;
    categorie: string | null;
    description: string | null;
    enStock: boolean;
  }>;
}

async function call<T>(method: 'GET' | 'PUT', path: string, body?: unknown): Promise<T> {
  const t = tokens.get();
  const r = await fetch(`${CORE_URL}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(t ? { Authorization: `Bearer ${t}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const json = (await r.json().catch(() => ({}))) as { data?: T; error?: ApiError };
  if (!r.ok) {
    throw new Error(json.error?.message ?? `HTTP ${r.status}`);
  }
  return json.data as T;
}

export const vitrineApi = {
  /** Lit l'état actuel de MA vitrine. */
  me: () => call<VitrineMe>('GET', '/vitrine/me'),

  /** Publie ou met à jour la vitrine (slogan/horaires/description + active). */
  publier: (input: Partial<VitrineMe> & { active: boolean }) =>
    call<{ active: boolean; updatedAt: number }>('PUT', '/vitrine', input),

  /** Lit la vitrine publique d'une boutique (pas d'auth). */
  lire: (boutiqueId: string) =>
    call<VitrinePublique>('GET', `/vitrine/public/${boutiqueId}`),
};
